import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './header';
import Footer from './footer';
import GeometricShapes from '../shared/GeometricShapes';
import Breadcrumb from '../ui/breadcrumb';
import ChatWidget from '../ui/chat-widget';
import ExclusiveModal from '../ui/exclusive-modal';
import HubSpotModal from '../ui/hubspot-modal';
import { useExclusiveModal } from '../../hooks/useExclusiveModal';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  breadcrumbs?: Array<{ name: string; href: string }>;
}

export default function Layout({
  children,
  title = 'recruteurs.io | Solutions RH innovantes pour entreprises et candidats',
  description = 'Découvrez Recruteurs.io, le cabinet RH qui allie IA et expertise humaine pour des recrutements plus rapides, alignés et durables.',
  keywords = 'recrutement, rh, ressources humaines, ia rh, externalisation rh, recrutement innovant',
  ogImage = '/og-image.jpg',
  ogType = 'website',
  breadcrumbs,
}: LayoutProps) {
  const fullTitle = title.includes('recruteurs.io') ? title : `${title} | recruteurs.io`;
  const canonicalUrl = typeof window !== 'undefined' ? `https://recruteurs.io${window.location.pathname}` : 'https://recruteurs.io';
  
  const { shouldShowModal, closeModal } = useExclusiveModal();
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  const handleContactClick = () => {
    closeModal();
    setIsHubSpotModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:type" content={ogType} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Preload critical assets */}
        <link 
          rel="preload" 
          href="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/IA-RH-MARSEILLE.webp" 
          as="image"
          fetchpriority="high"
        />
        <link 
          rel="preload" 
          href="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png"
          as="image"
        />
        
        {/* Preconnect to critical domains */}
        <link rel="preconnect" href="https://ygkvhunsrhvgddngoulm.supabase.co" />
        <link rel="preconnect" href="https://js.hsforms.net" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://forms.hsforms.com" crossOrigin="anonymous" />
        
        {/* Inline critical CSS */}
        <style>{`
          /* Critical CSS */
          .container-custom {
            width: 100%;
            max-width: 1440px;
            margin-left: auto;
            margin-right: auto;
            padding-left: 1rem;
            padding-right: 1rem;
          }
          @media (min-width: 768px) {
            .container-custom {
              padding-left: 2rem;
              padding-right: 2rem;
            }
          }
          @media (min-width: 1024px) {
            .container-custom {
              padding-left: 4rem;
              padding-right: 4rem;
            }
          }
          .bg-gradient-primary {
            background-image: linear-gradient(to right, #d03484, #d47254);
          }
        `}</style>
      </Helmet>

      <div className="flex flex-col min-h-screen bg-white text-neutral-900">
        <Header />
        <GeometricShapes />

        <main id="main-content" role="main" className="flex-grow pt-20">
          {breadcrumbs && <div className="container-custom"><Breadcrumb items={breadcrumbs} /></div>}
          {children}
        </main>

        <Footer />
        
        {/* Add ChatWidget */}
        <ChatWidget />
        
        {/* Exclusive Modal */}
        <ExclusiveModal
          isOpen={shouldShowModal}
          onClose={closeModal}
          onContactClick={handleContactClick}
        />
        
        {/* HubSpot Modal */}
        <HubSpotModal
          isOpen={isHubSpotModalOpen}
          onClose={() => setIsHubSpotModalOpen(false)}
        />
      </div>
    </>
  );
}